import log from 'loglevel';
import type { FileManagerConfig } from '@/types/fileManager';
import type { User } from '@/types/user';
import {
  LambdaOperationError,
  TransferFileRepository,
} from '@/repository/TransferFileRepository';
import type {
  TransferFileListResult,
  TransferFileSearchOptions,
} from '@/types/transferFile';

/** Error types thrown by TransferFileService. */
export type FileOperationErrorType =
  | 'ACCESS_DENIED'
  | 'NOT_FOUND'
  | 'INVALID_PATH'
  | 'UPLOAD_FAILED'
  | 'DELETE_FAILED';

/** Typed error for file operations. */
export class FileOperationError extends Error {
  constructor(
    public readonly type: FileOperationErrorType,
    message: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'FileOperationError';
  }
}

/** Maps Lambda error types to FileOperationError types. */
const LAMBDA_ERROR_TYPE_MAP: Record<string, FileOperationErrorType> = {
  INVALID_REQUEST: 'INVALID_PATH',
  UNAUTHORIZED_ACCESS: 'ACCESS_DENIED',
  NOT_FOUND: 'NOT_FOUND',
};

/**
 * Orchestrates file operations (list, upload, download, delete) via the FileManagerLambda.
 *
 * All S3 operations route through the Lambda. Path/filename validation is server-side.
 * User access and file size validation are client-side guards.
 */
export class TransferFileService {
  private readonly repository: TransferFileRepository;
  private readonly config: FileManagerConfig;

  constructor(config: FileManagerConfig) {
    this.config = config;
    this.repository = new TransferFileRepository();

    log.info('TransferFileService: Initialized with Lambda-based operations', {
      uploadPrefix: config.s3Prefixes?.uploadPrefix,
      activePrefix: config.s3Prefixes?.activePrefix,
      archivePrefix: config.s3Prefixes?.archivePrefix,
      maxFileSize: config.maxFileSize,
    });
  }

  /** Normalize path: strip leading/trailing slashes, ensure trailing slash for non-empty. */
  private normalizePath(path: string): string {
    // Handle empty path and root path
    if (!path || path === '/') {
      return '';
    }

    // Remove leading and trailing slashes
    const trimmed = path.replace(/^\/+|\/+$/g, '');

    // Return empty string if nothing left after trimming
    if (!trimmed) {
      return '';
    }

    // Ensure trailing slash for non-empty paths
    return `${trimmed}/`;
  }

  /** Throw ACCESS_DENIED if user lacks FileManagerAdmin or Admin role. */
  private validateUserAccess(user: User): void {
    if (!user.hasFileManagerAccess) {
      log.warn('TransferFileService: User access denied', {
        userId: user.userId,
        email: user.email,
      });
      throw new FileOperationError(
        'ACCESS_DENIED',
        'User does not have FileManager access',
        { userId: user.userId }
      );
    }
  }

  /** Throw UPLOAD_FAILED if file extension is in the blocked list. */
  private validateFileType(file: File): void {
    if (this.config.blockedFileTypes.length === 0) return;

    const ext = file.name.includes('.')
      ? `.${file.name.split('.').pop()?.toLowerCase()}`
      : '';

    if (ext && this.config.blockedFileTypes.includes(ext)) {
      throw new FileOperationError(
        'UPLOAD_FAILED',
        `File type "${ext}" is not allowed`,
        { fileName: file.name, blockedExtension: ext }
      );
    }
  }

  /** Throw UPLOAD_FAILED if file exceeds config.maxFileSize. */
  private validateFileSize(file: File): void {
    if (file.size > this.config.maxFileSize) {
      const maxSizeMB = (this.config.maxFileSize / (1024 * 1024)).toFixed(2);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      throw new FileOperationError(
        'UPLOAD_FAILED',
        `File size (${fileSizeMB} MB) exceeds maximum allowed size of ${maxSizeMB} MB`,
        { fileSize: file.size, maxFileSize: this.config.maxFileSize }
      );
    }

    log.debug('TransferFileService: File size validation passed', {
      fileSize: file.size,
      maxFileSize: this.config.maxFileSize,
    });
  }

  /** Map a caught error to a typed FileOperationError, using fallbackType for unknowns. */
  private mapLambdaError(
    error: unknown,
    fallbackType: FileOperationErrorType
  ): FileOperationError {
    // If it's already a FileOperationError, return as-is
    if (error instanceof FileOperationError) {
      return error;
    }

    // If it's a LambdaOperationError with a known type, map it
    if (error instanceof LambdaOperationError && error.lambdaErrorType) {
      const mappedType =
        LAMBDA_ERROR_TYPE_MAP[error.lambdaErrorType] ?? fallbackType;
      return new FileOperationError(mappedType, error.message, {
        lambdaErrorType: error.lambdaErrorType,
        operation: error.operation,
      });
    }

    // For generic errors, use the fallback type
    const message = error instanceof Error ? error.message : String(error);
    return new FileOperationError(fallbackType, message);
  }

  /** List files and virtual folders at the given path. */
  async listFiles(
    filePath: string,
    user: User,
    options: TransferFileSearchOptions = {}
  ): Promise<TransferFileListResult> {
    this.validateUserAccess(user);
    const normalizedPath = this.normalizePath(filePath);

    log.debug('TransferFileService: Listing files', {
      filePath: normalizedPath,
      userId: user.userId,
    });

    try {
      const result = await this.repository.listFiles(normalizedPath, {
        nextToken: options.nextToken,
        pageSize: options.limit,
      });

      return {
        files: result.files,
        folders: result.folders,
        nextToken: result.nextToken,
        total: result.files.length,
      };
    } catch (error) {
      log.error('TransferFileService: Failed to list files', {
        filePath: normalizedPath,
        error,
      });
      throw this.mapLambdaError(error, 'NOT_FOUND');
    }
  }

  /** Get a presigned download URL for the given S3 key. */
  async getDownloadUrl(key: string, user: User): Promise<string> {
    this.validateUserAccess(user);

    log.info('TransferFileService: Getting download URL', {
      key,
      userId: user.userId,
    });

    try {
      return await this.repository.getDownloadUrl(key);
    } catch (error) {
      log.error('TransferFileService: Failed to get download URL', {
        key,
        error,
      });
      throw this.mapLambdaError(error, 'NOT_FOUND');
    }
  }

  /** Upload a file via Lambda presigned PUT URL. Validates access and file size client-side. */
  async uploadFile(
    file: File,
    filePath: string,
    user: User,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; message: string }> {
    this.validateUserAccess(user);
    this.validateFileType(file);
    this.validateFileSize(file);
    const normalizedPath = this.normalizePath(filePath);

    log.info('TransferFileService: Uploading file', {
      filename: file.name,
      filePath: normalizedPath,
      originalPath: filePath,
      userId: user.userId,
    });

    try {
      await this.repository.uploadFile(file, normalizedPath, onProgress);
      return {
        success: true,
        message: 'Upload complete. File will appear after backend processing.',
      };
    } catch (error) {
      log.error('TransferFileService: Upload failed', {
        filename: file.name,
        error,
      });
      throw this.mapLambdaError(error, 'UPLOAD_FAILED');
    }
  }

  /** Delete a file (Lambda copies to archive prefix then removes the original). */
  async deleteFile(key: string, user: User): Promise<void> {
    this.validateUserAccess(user);

    log.info('TransferFileService: Deleting file', {
      key,
      userId: user.userId,
    });

    try {
      await this.repository.deleteFile(key);
    } catch (error) {
      log.error('TransferFileService: Delete failed', { key, error });
      throw this.mapLambdaError(error, 'DELETE_FAILED');
    }
  }
}
